import React from 'react';
import { BookingModality } from '../../models/types';
import { ModalityIncludeKind } from '../../utils/modalityIncludes';

const svgProps = {
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.6,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true as const,
};

/** Header icons — matched to the modality selection design. */
export const ModalityHeaderIcon: React.FC<{ modalityId: BookingModality }> = ({ modalityId }) => {
    if (modalityId === 'flexible') {
        // Sofa + speech bubble with exclamation
        return (
            <svg {...svgProps}>
                <path d="M4.5 14.5V12a1.5 1.5 0 0 1 1.5-1.5h12A1.5 1.5 0 0 1 19.5 12v2.5" />
                <path d="M3.5 14.5h17v3.2a1 1 0 0 1-1 1h-15a1 1 0 0 1-1-1v-3.2Z" />
                <path d="M7 18.7V20" />
                <path d="M17 18.7V20" />
                <path d="M8 10.5V9a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v1.5" />
                <path d="M15.2 3.2h3.6c.7 0 1.2.5 1.2 1.2v1.4c0 .7-.5 1.2-1.2 1.2h-1.1l-1.1.9v-.9h-1.4c-.7 0-1.2-.5-1.2-1.2V4.4c0-.7.5-1.2 1.2-1.2Z" />
                <path d="M17 4.55v1.1" />
                <circle cx="17" cy="6.55" r="0.35" fill="currentColor" stroke="none" />
            </svg>
        );
    }

    if (modalityId === 'asistida') {
        // Three people in front of a monitor
        return (
            <svg {...svgProps}>
                <rect x="3.5" y="3.5" width="17" height="11.5" rx="1.5" />
                <path d="M9 18.5h6" />
                <path d="M12 15v3.5" />
                <circle cx="8.2" cy="9.2" r="1.35" />
                <circle cx="12" cy="8.6" r="1.5" />
                <circle cx="15.8" cy="9.2" r="1.35" />
                <path d="M5.8 13.4v-.4a2.2 2.2 0 0 1 2.2-2.2h.4" />
                <path d="M9.3 13.5v-.7A2.6 2.6 0 0 1 12 10.2a2.6 2.6 0 0 1 2.7 2.6v.7" />
                <path d="M15.6 13.4v-.4a2.2 2.2 0 0 0-2.2-2.2h-.4" />
            </svg>
        );
    }

    // Party popper with confetti
    return (
        <svg {...svgProps}>
            <path d="M3.5 20.5 9.2 9.8l4.9 4.9Z" />
            <path d="M9.2 9.8c1.8-2.6 4.4-4.5 8.3-5.3-1.2 3.6-1.4 6.2.2 9.2" />
            <path d="M14.2 6.2 15 4.4" />
            <path d="M17.6 8.8 19.4 8" />
            <path d="M16.2 5.8 17.6 4.6" />
            <circle cx="13.2" cy="4.8" r="0.7" fill="currentColor" stroke="none" />
            <circle cx="19.2" cy="6.4" r="0.65" fill="currentColor" stroke="none" />
            <circle cx="18.4" cy="11.2" r="0.55" fill="currentColor" stroke="none" />
            <path d="M15.4 11.8 16.2 13.4" />
        </svg>
    );
};

/** Include icons — matched to the modality selection design. */
export const ModalityIncludeIcon: React.FC<{ kind: ModalityIncludeKind }> = ({ kind }) => {
    switch (kind) {
        case 'room':
            // House with gear inside
            return (
                <svg {...svgProps}>
                    <path d="M3 11 12 3.5 21 11" />
                    <path d="M5.5 9.8V20h13V9.8" />
                    <circle cx="12" cy="14.2" r="2.6" />
                    <path d="M12 11.2v1.1" />
                    <path d="M12 16.1v1.1" />
                    <path d="M9.4 14.2h1.1" />
                    <path d="M13.5 14.2h1.1" />
                    <path d="m10.2 12.4.8.8" />
                    <path d="m13 15.2.8.8" />
                    <path d="m13.8 12.4-.8.8" />
                    <path d="m11 15.2-.8.8" />
                </svg>
            );
        case 'merienda':
            // Cupcake with frosting swirl
            return (
                <svg {...svgProps}>
                    <path d="M12 2.8c.9 1.8 1 3.2.2 4.6" />
                    <path d="M8.2 9.4c-1.1.2-2 .9-2.3 1.9-.2.8.1 1.6.7 2.1" />
                    <path d="M15.8 9.4c1.1.2 2 .9 2.3 1.9.2.8-.1 1.6-.7 2.1" />
                    <path d="M7.2 13.4h9.6l-1.1 6.2a1.6 1.6 0 0 1-1.6 1.3H9.9a1.6 1.6 0 0 1-1.6-1.3L7.2 13.4Z" />
                    <path d="M7.8 9.6c1.2-1.4 2.5-2.1 4.2-2.1s3 .7 4.2 2.1c.4.5.2 1.2-.4 1.4H8.2c-.6-.2-.8-.9-.4-1.4Z" />
                    <path d="M9.4 16h5.2" />
                    <path d="M9.1 18.2h5.8" />
                </svg>
            );
        case 'cleaning_room':
            // Angled broom
            return (
                <svg {...svgProps}>
                    <path d="M14.2 3.2 9.4 13.8" />
                    <path d="M8.1 13.2c-1.6.9-2.8 2.4-3.2 4.3-.2 1 .5 1.9 1.5 1.9h7.4c.9 0 1.5-.9 1.2-1.8-.6-2-1.9-3.6-3.6-4.5" />
                    <path d="M6.4 16.2h7.4" />
                    <path d="M7.2 18.4h5.8" />
                    <path d="M8.6 13.5 7.2 15.8" />
                    <path d="M10.2 13.9 9.4 16.2" />
                    <path d="M11.8 13.6 12.2 16" />
                </svg>
            );
        case 'cleaning_terrace':
            // Potted plant
            return (
                <svg {...svgProps}>
                    <path d="M9.2 20.5h5.6l.9-4.8H8.3l.9 4.8Z" />
                    <path d="M8 15.7h8" />
                    <path d="M12 15.7V9.2" />
                    <path d="M12 12.2c-2.2-.2-3.8-1.6-4.2-3.5 1.9-.1 3.4 1 4.2 3.5Z" />
                    <path d="M12 11.4c2-.4 3.4-1.9 3.7-3.7-1.8.2-3.2 1.3-3.7 3.7Z" />
                    <path d="M12 9.4c-.4-2 .4-3.8 2.1-4.8-.2 2.1-1 3.6-2.1 4.8Z" />
                    <path d="M12 9.8c.5-1.9-.2-3.8-1.8-4.9.1 2 .8 3.6 1.8 4.9Z" />
                </svg>
            );
        case 'decoration':
            // Heart inside a circle
            return (
                <svg {...svgProps}>
                    <circle cx="12" cy="12" r="8.2" />
                    <path d="M12 16.4s-4.4-2.7-4.4-5.6A2.5 2.5 0 0 1 12 8.6a2.5 2.5 0 0 1 4.4 2.2c0 2.9-4.4 5.6-4.4 5.6Z" />
                </svg>
            );
        case 'monitor':
            // Person bust inside a circular frame
            return (
                <svg {...svgProps}>
                    <circle cx="12" cy="12" r="8.2" />
                    <circle cx="12" cy="10" r="2.4" />
                    <path d="M7.4 17.2v-.5A4 4 0 0 1 11.4 12.7h1.2a4 4 0 0 1 4 4v.5" />
                </svg>
            );
        default:
            return (
                <svg {...svgProps}>
                    <circle cx="12" cy="12" r="8" />
                    <path d="m9 12 2 2 4-4" />
                </svg>
            );
    }
};
